import { Link } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { Layers } from 'lucide-react';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';
import { useIncident, useIncidents, type Incident } from '@/hooks/useIncidents';

/**
 * Incident rollup on the detail page (`docs/IncidentRollup.md`), both directions.
 *
 * - **A child** says what it is filed under, and — the whole point — that it is still
 *   open. "Rolled up" must never read as "resolved": the problem is still there, a broader
 *   incident just explains it.
 * - **A parent** lists what is filed under it, open first, with resolved members kept as
 *   history.
 *
 * Renders nothing for the common incident, which is neither.
 */
export function IncidentRollupPanel({ incident }: { incident: Incident }) {
  const { data: parent } = useIncident(incident.rolledUpIntoId ?? undefined);
  const { data: members } = useIncidents(
    incident.organizationId,
    // A parent's members are listed whatever their state, so the list does not change
    // meaning under the user when one of them resolves.
    { rolledUpInto: incident.id, includeArchived: true },
  );
  const sorted = [...(members ?? [])].sort((a, b) => Number(a.resolvedAt !== null) - Number(b.resolvedAt !== null));
  const openMembers = sorted.filter((m) => m.resolvedAt === null).length;
  const parentId = incident.resolvedAt === null ? incident.rolledUpIntoId : null;

  if (parentId === null && sorted.length === 0) return null;

  return (
    <section className="bg-card border border-border rounded-lg p-5 space-y-3">
      {parentId !== null ? (
        <div>
          <h2 className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Layers className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            Rolled up into a broader incident
          </h2>
          <p className="text-xs text-muted-foreground mb-2">
            This incident is <strong className="text-foreground">still open</strong>. A broader
            incident explains it, so it is listed there instead of on its own and does not notify
            separately. If that incident resolves while this is still failing, this one stands on its
            own again.
          </p>
          <Link
            to={`/incidents/${parentId}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border-subtle px-3 py-2 text-sm hover:bg-accent"
          >
            <span className="font-medium truncate flex-1 min-w-0">{parent?.title ?? 'View the broader incident'}</span>
            {parent ? <SeverityBadge severity={parent.severity} /> : null}
          </Link>
        </div>
      ) : null}

      {sorted.length > 0 ? (
        <div>
          <h2 className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Layers className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            Rolled up here
          </h2>
          <p className="text-xs text-muted-foreground mb-2">
            {openMembers === 0
              ? 'Everything that was rolled up here has since resolved.'
              : `${openMembers === 1 ? '1 related incident is' : `${String(openMembers)} related incidents are`} still open and explained by this one. They stay open, and are not notified separately.`}
          </p>
          <ul className="space-y-1.5">
            {sorted.map((member) => (
              <li key={member.id}>
                <Link
                  to={`/incidents/${member.id}`}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border-subtle px-3 py-2 text-sm hover:bg-accent"
                >
                  <span className="font-medium truncate flex-1 min-w-0">{member.title}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {member.resolvedAt
                      ? `Resolved ${formatDistanceToNow(new Date(member.resolvedAt), { addSuffix: true })}`
                      : `Opened ${formatDistanceToNow(new Date(member.createdAt), { addSuffix: true })}`}
                  </span>
                  <SeverityBadge severity={member.severity} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
